import PageHeaderActions from "Components/Common/PageHeaderActions";
import { StatsCardProps } from "Components/Common/StatsCard";
import StatsGroup from "Components/Common/StatsGroup";
import OverlayGuide from "Components/Common/TourGuide/OverlayGuide";
import { useAppDispatch, useAppSelector } from "Components/Hooks/hooks";
import HowCreateQuotesModal from "Components/Modals/HowCreateQuotesModal";
import SelectPresetQuoteModal from "Components/Modals/SelectPresetQuoteModal";
import RestrictedAccessModal from "Components/Modals/RestrictedAccessModal";
import QuoteList from "Components/Quotes/QuoteList";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Container } from "reactstrap";
import { clearItemState } from "slices/items/reducer";
import { clearQuoteDetails } from "slices/quotes/reducer";
import { tourElementsIds } from "tour/tourElementsIds";
import { useScreenTour } from "tour/useScreenTour";
import { IconName } from "Components/Icons/Icon";
import { ErrorToast } from "utils/toast";

const Quotes = () => {
  const dispatch = useAppDispatch();
  const [showHowCreateQuoteModal, setShowHowCreateQuoteModal] = useState(false);
  const [showSelectPresetModal, setShowSelectPresetModal] = useState(false);
  const [showSubscriptionExpiredPopUp, setShowSubscriptionExpiredPopUp] =
    useState(false);
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.auth.user);
  const hasNotSubscribed =
    user?.is_subscription_active === false && user.is_trial_period === false;
  const hasBankAccount = user?.hasBankAccountDetailAdded;
  const hasSignature = user?.hasSignatureAdded;
  const { presetQuoteList } = useAppSelector((state) => state?.presetQuote);

  const [restrictionModalData, setRestrictionModalData] = useState<{
    iconName: IconName;
    title: string;
    description: string;
    buttonText: string;
    onAction: () => void;
  }>({
    iconName: "" as IconName,
    title: "",
    description: "",
    buttonText: "",
    onAction: () => {},
  });

  const RESTRICTION_CONFIG = {
    SUBSCRIPTION_REQUIRED: {
      iconName: "SubscriptionRequired",
      title: "Subscription Required",
      description:
        "To continue using EaziQuote, please activate or renew your subscription.",
      buttonText: "Activate Subscription",
      onAction: () => {
        if (user?.is_team_member) {
          ErrorToast(
            "Only the account owner can manage subscriptions. Please contact your owner."
          );
        } else {
          navigate("/subscribe-plan");
        }
      },
    },
    BANK_ACCOUNT_REQUIRED: {
      iconName: "Info",
      title: "Add Bank Details",
      description:
        "Including bank details makes it easier for clients to pay you without follow-ups.",
      buttonText: "Add Bank Details",
      onAction: () => navigate("/payments-and-invoicing"),
    },
    SIGNATURE_REQUIRED: {
      iconName: "Info",
      title: "Add Your Signature",
      description:
        "Adding a signature helps build trust and makes your quote feel complete and professional.",
      buttonText: "Add Signature",
      onAction: () =>
        navigate("/payments-and-invoicing", {
          state: {
            signatureMissing: true,
          },
        }),
    },
    BANK_AND_SIGNATURE_REQUIRED: {
      iconName: "Info",
      title: "Complete Your Setup",
      description:
        "Add your bank details and signature to ensure your quotes look professional and include payment information.",
      buttonText: "Complete Setup",
      onAction: () => navigate("/payments-and-invoicing"),
    },
  } as const;
  document.title = "Quotes | EaziQuote";

  const quoteTour = useScreenTour("quote", {
    onExtraClose: () => setShowHowCreateQuoteModal(false),
    onExtraNext: () => setShowHowCreateQuoteModal(true),
    onExtraPrev: () => setShowHowCreateQuoteModal(false),
  });

  const summary = useAppSelector((s) => s.quote.summary);

  const quoteStatsData: StatsCardProps[] = [
    {
      title: "TOTAL QUOTES",
      icon: "BigBlueReceipt",
      value: summary?.total_count || 0,
      suffix: "",
      prefix: "",
      separator: ",",
      decimal: 0,
    },
    {
      title: "ACCEPTED",
      icon: "BigGreenTickMark",
      value: summary?.accepted_count || 0,
      suffix: "",
      prefix: "",
      separator: ",",
      decimal: 0,
    },
    {
      title: "PENDING",
      icon: "BigOrangeWatch",
      value: summary?.pending_count || 0,
      suffix: "",
      prefix: "",
      separator: ",",
      decimal: 0,
    },
    {
      title: "EXPIRED",
      icon: "BigRedSandWatch",
      value: summary?.expired_count || 0,
      suffix: "",
      prefix: "",
      separator: ",",
      decimal: 0,
    },
  ];

  return (
    <div className="page-content">
      <Container fluid>
        <div className="gap-3 d-flex flex-column">
          <PageHeaderActions
            title="Quotes"
            subtitle="Manage all your quotes in one place"
            id={tourElementsIds.quoteTour1}
            actions={[
              {
                label: "New Quote",
                onClick: () => {
                  if (hasNotSubscribed) {
                    setShowSubscriptionExpiredPopUp(true);
                    setRestrictionModalData(
                      RESTRICTION_CONFIG.SUBSCRIPTION_REQUIRED
                    );
                  } else if (!hasBankAccount && !hasSignature) {
                    setShowSubscriptionExpiredPopUp(true);
                    setRestrictionModalData(
                      RESTRICTION_CONFIG.BANK_AND_SIGNATURE_REQUIRED
                    );
                  } else if (!hasBankAccount) {
                    setShowSubscriptionExpiredPopUp(true);
                    setRestrictionModalData(
                      RESTRICTION_CONFIG.BANK_ACCOUNT_REQUIRED
                    );
                  } else if (!hasSignature) {
                    setShowSubscriptionExpiredPopUp(true);
                    setRestrictionModalData(
                      RESTRICTION_CONFIG.SIGNATURE_REQUIRED
                    );
                  } else {
                    setShowHowCreateQuoteModal(true);
                  }
                },
                leftIcon: <i className="ri-add-fill me-1 align-bottom"></i>,
              },
            ]}
          />
          <StatsGroup items={quoteStatsData} />
          <QuoteList />
          <HowCreateQuotesModal
            show={showHowCreateQuoteModal}
            onStartFromScratchClick={() => {
              setShowHowCreateQuoteModal(false);
              dispatch(clearQuoteDetails());
              dispatch(clearItemState());
              navigate("/manage-quotes");
            }}
            onUsePresetClick={() => {
              setShowSelectPresetModal(true);
              setShowHowCreateQuoteModal(false);
            }}
            onCloseClick={() => {
              setShowHowCreateQuoteModal(false);
            }}
          />

          <SelectPresetQuoteModal
            isOpen={showSelectPresetModal}
            onClose={() => setShowSelectPresetModal(false)}
            onContinue={(template) => {
              console.log("Selected Template", template);
              if (!presetQuoteList.length) {
                ErrorToast(
                  "No preset quote available. Please create a preset quote first."
                );
                return;
              }
              if (template === null) {
                ErrorToast("Please select at least one preset quote.");
                return;
              }
              dispatch(clearQuoteDetails());
              dispatch(clearItemState());
              navigate("/manage-quotes", {
                state: { template },
              });
              setShowSelectPresetModal(false);
            }}
          />
        </div>
        <OverlayGuide {...quoteTour} />
        <RestrictedAccessModal
          isOpen={showSubscriptionExpiredPopUp}
          onCancel={() => setShowSubscriptionExpiredPopUp(false)}
          iconName={restrictionModalData.iconName}
          title={restrictionModalData.title}
          description={restrictionModalData.description}
          buttonText={restrictionModalData.buttonText}
          onButtonClick={restrictionModalData.onAction}
        />
      </Container>
    </div>
  );
};

export default Quotes;
